import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { apiGet } from '../../lib/api-client';
import { useDirty } from '../../lib/dirty';
import { listQueryString, neighbors, type RecordStep } from '../../lib/record-step';

export function RecordStepper({ listUrl, id, basePath, defaultFilter = 'all' }: {
  listUrl: string;
  id: string;
  basePath: string;
  defaultFilter?: string;
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const dirty = useDirty();
  const prefer = location.pathname.startsWith('/quotations/') ? 'quotation' : location.pathname.startsWith('/proposals/') ? 'proposal' : '';
  const [nav, setNav] = useState<{ prev: string; next: string; label: string; search: string } | null>(null);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    async function load(filter: string | undefined, allowFallback: boolean) {
      const params = new URLSearchParams(listQueryString(location.search).slice(1));
      if (filter) params.set('filter', filter);
      else if (!params.get('filter') && defaultFilter && defaultFilter !== 'all') params.set('filter', defaultFilter);
      if (params.get('filter') === 'all') params.delete('filter');
      const [path, existing] = listUrl.split('?');
      const merged = new URLSearchParams(existing || '');
      params.forEach((value, key) => merged.set(key, value));
      merged.set('index', '1');
      merged.set('page', '1');
      merged.set('pageSize', 'all');
      const data = await apiGet<{ steps: RecordStep[] }>(`${path}?${merged}`);
      const found = neighbors(data.steps || [], id, basePath, prefer);
      if (!found && allowFallback && params.get('filter') && params.get('filter') !== 'all') {
        await load('all', false);
        return;
      }
      const kept = new URLSearchParams(params);
      kept.delete('index');
      kept.delete('page');
      kept.delete('pageSize');
      if (!cancelled) setNav(found ? { ...found, search: kept.toString() ? `?${kept}` : '' } : null);
    }
    void load(undefined, true).catch(() => { if (!cancelled) setNav(null); });
    return () => { cancelled = true; };
  }, [id, listUrl, basePath, defaultFilter, prefer, location.search]);

  if (!nav) return null;

  function go(path: string) {
    if (!path) return;
    dirty.requestLeave(() => navigate(`${path}${nav?.search || ''}`));
  }

  const button = 'inline-flex items-center gap-1 rounded-md border border-[var(--bo-border)] bg-[var(--bo-surface)] px-3 py-1.5 text-sm text-[var(--bo-text)] hover:bg-[var(--bo-hover)] disabled:cursor-default disabled:opacity-40 disabled:hover:bg-[var(--bo-surface)]';
  return (
    <div className="flex items-center justify-end gap-2">
      <button type="button" className={button} disabled={!nav.prev} onClick={() => go(nav.prev)}>
        <ChevronLeft className="h-4 w-4" />
        Previous
      </button>
      <span className="min-w-16 text-center text-sm text-[var(--bo-muted)]">{nav.label}</span>
      <button type="button" className={button} disabled={!nav.next} onClick={() => go(nav.next)}>
        Next
        <ChevronRight className="h-4 w-4" />
      </button>
    </div>
  );
}
